import './utils/buttons.css';
import { isContractDisabled, getStatusColor } from './utils/contractStatus';
import React, { useState, useEffect, useCallback, Fragment, useRef } from 'react';
import ConfirmationModal from '../../templates/ConfirmationModal';
import { useSignal } from "@preact/signals-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import { FaSearch, FaSave, FaBan, FaCheck, FaEllipsisV } from "react-icons/fa";
import PageForm from "../../templates/PageForm";
import CurrencyInput from "../../molecules/CurrencyInput";
import { applyDateMask } from '../../../utils/dateMask';
import TextEdit from "../../molecules/TextEdit";
import BadgeStatus from "../../atoms/BadgeStatus";
import ReceivablesDownloader from "./components/ReceivablesDownloader";
import DuplicateModalView from "./modals/DuplicateModalView";
import ContractFileUpload from './components/ContractFileUpload';
import ArtifactFilesUpload from './components/ArtifactFilesUpload';
import PersonSearchModal from "./modals/PersonSearchModal";
import DuplicatesTable from './components/DuplicatesTable';
import DisbursementManager from './components/DisbursementManager';
import { DuplicateItem } from './types/operations';
import { PersonSearchResult, JsonUploadData } from './types/api-responses';
import { useFileDownload } from './components/useFileDownload';
import appConfig from '../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.contractsManagementApiEnv;

type Disbursement = {
    amount: number;
    date: string;
};

type Form = {
    total_amount: number;
    loan_amount: number;
    expiration_date: string;
    emission_date: string;
    created_at: string;
    guarantee_percentage: number;
    operation_number: string;
    status?: string;
    sequencial_number: number;
    external_link: string;
    type: string;
    assignor: string;
    recipient: string;
    originator: string;
    rebate_amount: number;
    assignment_type_name: string;
    with_recourse: boolean;
    duplicates: {
        quantity: number;
        total: number;
        items: DuplicateItem[];
    };
    disbursement: {
        disbursements: Disbursement[];
        totalAmount: number;
    };
};

const ReceivablesAssignmentPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const credit_operation_id = params.credit_operation_id || params.id;
    const { handleDownload } = useFileDownload();
    const isLoadingForm = useSignal(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const apiVersion = useSignal('');

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleCancelContract = async () => {
        try {
            isLoadingForm.value = true;
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${API_URL}/contracts/${credit_operation_id}/status/CANCELED`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY || ''
                }
            });
            if (response.ok) {
                toast.success('Contrato cancelado com sucesso!');
                navigate('/contracts/0/100/id/ASC/s=;');
            } else {
                toast.error('Erro ao cancelar contrato');
            }
        } catch (error) {
            toast.error('Erro ao conectar com o servidor');
        } finally {
            isLoadingForm.value = false;
            setIsCancelModalOpen(false);
        }
    };

    const handleFinishContract = async () => {
        try {
            isLoadingForm.value = true;
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${API_URL}/contracts/${credit_operation_id}/status/FINISHED`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY || ''
                }
            });
            if (response.ok) {
                toast.success('Contrato finalizado com sucesso!');
                navigate('/contracts/0/100/id/ASC/s=;');
            } else {
                toast.error('Erro ao finalizar contrato');
            }
        } catch (error) {
            toast.error('Erro ao conectar com o servidor');
        } finally {
            isLoadingForm.value = false;
            setIsFinishModalOpen(false);
        }
    };
    const [selectedDestinatario, setSelectedDestinatario] = useState('');
    const [selectedCedente, setSelectedCedente] = useState('');
    const [assignorData, setAssignorData] = useState<any>(null);
    const [recipientData, setRecipientData] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const contractFiles = useSignal<File[]>([]);
    const artifactFiles = useSignal<File[]>([]);
    const [existingContractFile, setExistingContractFile] = useState<string>('');
    const [existingArtifactFiles, setExistingArtifactFiles] = useState<string[]>([]);
    const [removedContractFile, setRemovedContractFile] = useState<boolean>(false);
    const [removedArtifactFiles, setRemovedArtifactFiles] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;
    const [selectedDuplicate, setSelectedDuplicate] = useState<DuplicateItem | null>(null);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

    const form = useSignal<Form>({
        total_amount: 0,
        loan_amount: 0,
        expiration_date: '',
        emission_date: '',
        created_at: '',
        guarantee_percentage: 0,
        operation_number: "",
        sequencial_number: 0,
        external_link: "",
        type: "",
        assignor: "",
        recipient: "",
        originator: "",
        rebate_amount: 0,
        assignment_type_name: "",
        with_recourse: false,
        duplicates: {
            quantity: 0,
            total: 0,
            items: []
        },
        disbursement: {
            disbursements: [],
            totalAmount: 0
        }
    });

    const handleChange = useCallback((field: string, value: string | number | boolean) => {
        form.value = { ...form.value, [field]: value };
    }, []);

    const handleDuplicateChange = useCallback((index: number, field: string, value: string | number) => {
        const items = [...form.value.duplicates.items];
        items[index] = { ...items[index], [field]: value };
        const total = items.reduce((sum, item) => sum + item.receivable_amount, 0);
        form.value = {
            ...form.value,
            duplicates: { ...form.value.duplicates, items, total }
        };
    }, []);

    const updateDuplicatesWithFormData = useCallback((items: any[]) => {
        return items.map((item: any) => ({
            document_id: item.document_id || "",
            document_title: item.document_title || "",
            cfop: item.cfop || "",
            issue_date: item.issue_date || "",
            xml_size_bytes: item.xml_size_bytes || 0,
            assignor_name: item.assignor_name || "",
            assignor_cnpj: item.assignor_cnpj || "",
            debtor_cpf_cnpj: item.debtor_cpf_cnpj || "",
            debtor_name: item.debtor_name || "",
            debtor_email: item.debtor_email || "",
            address_street: item.address_street || "",
            address_number: item.address_number || "",
            address_complement: item.address_complement || "",
            address_district: item.address_district || "",
            address_ibge_code: item.address_ibge_code || "",
            address_city: item.address_city || "",
            address_state: item.address_state || "",
            address_zip_code: item.address_zip_code || "",
            address_country: item.address_country || "",
            address_phone: item.address_phone || "",
            receivable_number: item.receivable_number || "",
            receivable_due_date: item.receivable_due_date || "",
            receivable_amount: item.receivable_amount || 0,
            receivable_v_pag: item.receivable_v_pag || "",
            receivable_nat_op: item.receivable_nat_op || "",
            receivable_ind_pag: item.receivable_ind_pag || "",
            disbursement_value: item.disbursement_value || 0,
            fee_name: item.fee_name || "",
            fee_value: item.fee_value || 0,
            guarantee_percent: item.guarantee_percent || 0,
            main_risk: item.main_risk || "",
            external_link: form.value.external_link || ""
        }));
    }, []);

    const handleJsonDataLoaded = (data: JsonUploadData) => {
        if (data.duplicates?.items) {
            const updatedItems = data.duplicates.items.map((item: any) => ({
                document_id: item.document_id || "",
                document_title: item.document_title || "",
                cfop: item.cfop || "",
                issue_date: item.issue_date || "",
                xml_size_bytes: item.xml_size_bytes || 0,
                assignor_name: item.assignor_name || "",
                assignor_cnpj: item.assignor_cnpj || "",
                debtor_cpf_cnpj: item.debtor_cpf_cnpj || "",
                debtor_name: item.debtor_name || "",
                debtor_email: item.debtor_email || "",
                address_street: item.address_street || "",
                address_number: item.address_number || "",
                address_complement: item.address_complement || "",
                address_district: item.address_district || "",
                address_ibge_code: item.address_ibge_code || "",
                address_city: item.address_city || "",
                address_state: item.address_state || "",
                address_zip_code: item.address_zip_code || "",
                address_country: item.address_country || "",
                address_phone: item.address_phone || "",
                receivable_number: item.receivable_number || "",
                receivable_due_date: item.receivable_due_date || "",
                receivable_amount: item.receivable_amount || 0,
                receivable_v_pag: item.receivable_v_pag || "",
                receivable_nat_op: item.receivable_nat_op || "",
                receivable_ind_pag: item.receivable_ind_pag || "",
                disbursement_value: item.disbursement_value || 0,
                fee_name: item.fee_name || "",
                fee_value: item.fee_value || 0,
                guarantee_percent: item.guarantee_percent || 0,
                main_risk: item.main_risk || "",
                external_link: item.external_link || ""
            }));

            form.value = {
                ...form.value,
                total_amount: data.total_amount || form.value.total_amount,
                operation_number: data.operation_code || form.value.operation_number,
                duplicates: {
                    quantity: updatedItems.length,
                    total: updatedItems.reduce((sum, item) => sum + item.receivable_amount, 0),
                    items: updatedItems
                }
            };
        }
    };





    const handleSubmit = async () => {
        if (isFormDisabled) {
            toast.error('Não é possível salvar. Status não permite alterações.');
            return;
        }

        if (!selectedDestinatario) {
            toast.error('Beneficiário é obrigatório');
            return;
        }

        if (!API_URL || !API_KEY) {
            toast.error('Configuração da API não encontrada');
            return;
        }

        if (!form.value.external_link || form.value.external_link.trim() === '') {
            toast.error('Card JIRA é obrigatório');
            return;
        }

        // Validar Valor Nominal obrigatório
        if (!form.value.disbursement.totalAmount || form.value.disbursement.totalAmount <= 0) {
            toast.error('Valor Nominal é obrigatório');
            return;
        }

        // Validar se soma dos desembolsos não excede o Valor Nominal
        const sumDisbursements = form.value.disbursement.disbursements.reduce((sum, d) => sum + d.amount, 0);
        if (sumDisbursements > form.value.disbursement.totalAmount) {
            toast.error(`Soma dos desembolsos (R$ ${sumDisbursements.toFixed(2)}) não pode exceder o Valor Nominal (R$ ${form.value.disbursement.totalAmount.toFixed(2)})`);
            return;
        }

        const sanitizeFileName = (fileName: string): string => {
            return fileName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
        };

        isLoadingForm.value = true;
        try {
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const payload = {
                type: form.value.type || "",
                assignor: selectedCedente || "00000000-0000-0000-0000-000000000000", // UUID do cedente
                recipient: recipientData.id || "00000000-0000-0000-0000-000000000000", // UUID do destinatário
                credit_operation_id: credit_operation_id,
                operation_number: form.value.operation_number || "",
                originator: form.value.originator || "",
                operation_type: "RECEIVABLES_ASSIGNMENT",
                operation_schema_id: 2,
                approved_on: null,
                approved_value: null,
                current_balance: null,
                debt_balance_id: null,
                nominal_value: form.value.disbursement.totalAmount || 0,
                loan_amount: form.value.loan_amount || 0,
                assignment_type_name: form.value.assignment_type_name || "",
                with_recourse: form.value.with_recourse,
                due_date: form.value.expiration_date || null,
                participants: [
                    {
                        id: assignorData?.id || null,
                        name: assignorData?.name || form.value.assignor,
                        document: assignorData?.document || "",
                        is_active: true,
                        person_id: selectedCedente || "00000000-0000-0000-0000-000000000000",
                        is_primary: true,
                        effective_to: null,
                        effective_from: assignorData?.effective_from || new Date().toISOString(),
                        guarantee_value: null,
                        participant_type_id: 3,
                        participation_order: 1,
                        guarantee_percentage: form.value.guarantee_percentage,
                        participant_type_name: "ASSIGNOR",
                        receives_disbursement: false
                    },
                    {
                        id: recipientData?.id || null,
                        name: recipientData?.name || form.value.recipient,
                        document: recipientData?.document || "",
                        is_active: recipientData?.is_active || true,
                        person_id: selectedDestinatario || "00000000-0000-0000-0000-000000000000",
                        is_primary: recipientData?.is_primary || false,
                        effective_to: recipientData?.effective_to || null,
                        effective_from: recipientData?.effective_from || new Date().toISOString(),
                        guarantee_value: null,
                        participant_type_id: 5,
                        participation_order: 1,
                        guarantee_percentage: null,
                        participant_type_name: "RECIPIENT",
                        receives_disbursement: false
                    }
                ],
                operation_dictionary: {
                    duplicates: {
                        items: form.value.duplicates.items.map(item => ({
                            document_id: item.document_id || "",
                            document_title: item.document_title || "",
                            cfop: item.cfop || "",
                            issue_date: item.issue_date || "",
                            xml_size_bytes: item.xml_size_bytes || 0,
                            assignor_name: item.assignor_name || "",
                            assignor_cnpj: item.assignor_cnpj || "",
                            debtor_cpf_cnpj: item.debtor_cpf_cnpj || "",
                            debtor_name: item.debtor_name || "",
                            debtor_email: item.debtor_email || "",
                            address_street: item.address_street || "",
                            address_number: item.address_number || "",
                            address_complement: item.address_complement || "",
                            address_district: item.address_district || "",
                            address_ibge_code: item.address_ibge_code || "",
                            address_city: item.address_city || "",
                            address_state: item.address_state || "",
                            address_zip_code: item.address_zip_code || "",
                            address_country: item.address_country || "",
                            address_phone: item.address_phone || "",
                            receivable_number: item.receivable_number || "",
                            receivable_due_date: item.receivable_due_date || "",
                            receivable_amount: item.receivable_amount || 0,
                            receivable_v_pag: item.receivable_v_pag || "",
                            receivable_nat_op: item.receivable_nat_op || "",
                            receivable_ind_pag: item.receivable_ind_pag || "",
                            disbursement_value: item.disbursement_value || 0,
                            fee_name: item.fee_name || "",
                            fee_value: item.fee_value || 0,
                            guarantee_percent: item.guarantee_percent || 0,
                            main_risk: item.main_risk || "",
                            external_link: item.external_link || ""
                        })),
                        total: form.value.duplicates.total || 0,
                        quantity: form.value.duplicates.quantity || 1
                    },
                    disbursement: {
                        totalAmount: form.value.disbursement.totalAmount || null,
                        disbursements: form.value.disbursement.disbursements.map(d => ({
                            date: d.date || null,
                            amount: d.amount || null
                        }))
                    },
                    total_amount: form.value.total_amount || null,
                    emission_date: form.value.emission_date || null,
                    external_link: form.value.external_link || "",
                    rebate_amount: form.value.rebate_amount || null,
                    expiration_date: form.value.expiration_date || null,
                    operation_number: form.value.operation_number || "",
                    guarantee_percentage: form.value.guarantee_percentage || null,
                    files: {
                        // Usar novo arquivo ou manter existente se não foi removido
                        contract_file: contractFiles.value.length > 0
                            ? `${form.value.sequencial_number}${sanitizeFileName(contractFiles.value[0].name.substring(contractFiles.value[0].name.lastIndexOf('.')))}`
                            : (removedContractFile ? '' : existingContractFile),
                        // Manter artefatos existentes não removidos e adicionar novos
                        artefacts: [
                            ...existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)),
                            ...artifactFiles.value.map(file => sanitizeFileName(file.name))
                        ]
                    },
                }
            };

            const headers: Record<string, string> = {
                'Authorization': `Bearer ${token}`,
                'x-api-key': API_KEY || '',
                'Content-Type': 'application/json'
            };

            const response = await fetch(`${API_URL}/contracts/receivables_assignment/update`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Response data:', data);

                if (data.upload_urls && Object.keys(data.upload_urls).length > 0) {
                    const uploadEntries = Object.entries(data.upload_urls);
                    toast.info(`Enviando ${uploadEntries.length} arquivo(s)...`);

                    const uploadPromises = uploadEntries.map(async ([fileName, uploadInfo]: [string, any]) => {
                        let fileToUpload: File | undefined;

                        if (contractFiles.value.length > 0) {
                            fileToUpload = contractFiles.value[0];
                        } else {
                            fileToUpload = artifactFiles.value.find(f => fileName.includes(sanitizeFileName(f.name)));
                        }

                        if (fileToUpload) {
                            console.log(`Uploading file: ${fileName} to ${uploadInfo.upload_url}`);
                            const uploadResponse = await fetch(uploadInfo.upload_url, {
                                method: 'PUT',
                                body: fileToUpload,
                                headers: { 'Content-Type': 'application/octet-stream' }
                            });
                            console.log(`Upload response status: ${uploadResponse.status}`);
                            if (!uploadResponse.ok) {
                                console.error(`Upload failed for ${fileName}:`, await uploadResponse.text());
                                throw new Error(`Upload failed for ${fileName}`);
                            }
                            return uploadResponse;
                        }
                    });

                    await Promise.all(uploadPromises);
                    toast.success('Arquivos enviados com sucesso!');
                } else {
                    console.log('No upload_urls in response');
                }

                toast.success("Cessão de recebíveis atualizada com sucesso!");
                await fetchContractData();
                contractFiles.value = [];
                artifactFiles.value = [];
                setRemovedContractFile(false);
                setRemovedArtifactFiles([]);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao salvar cessão');
            }
        } catch (error) {
            toast.error("Erro ao conectar com o servidor");
        } finally {
            isLoadingForm.value = false;
        }
    };



    const handleSelectPerson = (selected: PersonSearchResult) => {
        const cnpjDoc = selected.documents?.find((doc) => doc.type === 'CNPJ');
        const cpfDoc = selected.documents?.find((doc) => doc.type === 'CPF');
        const document = cnpjDoc?.number || cpfDoc?.number || '';

        if (!document || document.trim() === '') {
            toast.error("Beneficiário sem documento cadastrado");
            return;
        }

        setSelectedDestinatario(selected.uuid);
        handleChange("recipient", selected.name || '');

        setRecipientData({
            ...recipientData,
            name: selected.name,
            document: document,
            person_id: selected.uuid,
            is_active: true,
            is_primary: false,
            effective_from: new Date().toISOString()
        });
    };

    const fetchContractData = async () => {
        if (!credit_operation_id || !API_URL || !API_KEY) {
            return;
        }

        try {
            isLoadingForm.value = true;
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const response = await fetch(`${API_URL}/contracts/receivables_assignment/${credit_operation_id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY || ''
                }
            });

            if (response.ok) {
                const data = await response.json();
                const schema = data.operation_dictionary || {};

                // Set API version info if available
                if (data.apiName && data.apiVersion) {
                    apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
                }

                const assignorParticipant = data.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR');
                const recipientData = data.participants?.find((p: any) => p.participant_type_name === 'RECIPIENT');
                form.value = {
                    ...form.value,
                    operation_number: data.operation_number,
                    status: data.status || '',
                    sequencial_number: data.sequencial_number || 0,
                    assignor: assignorParticipant?.name || '',
                    originator: data.originator || '',
                    with_recourse: data.with_recourse || false,
                    assignment_type_name: data.assignment_type || '',
                    created_at: data.created_at ? new Date(data.created_at).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR'),
                    expiration_date: schema.expiration_date || '',
                    emission_date: schema.emission_date || '',
                    total_amount: schema.total_amount || data.nominal_value || 0,
                    loan_amount: data.loan_amount || 0,
                    external_link: schema.external_link || '',
                    rebate_amount: schema.rebate_amount || 0,
                    guarantee_percentage: schema.guarantee_percentage || assignorParticipant?.guarantee_percentage || 0,
                    duplicates: {
                        quantity: schema.duplicates?.quantity || 0,
                        total: schema.duplicates?.total || 0,
                        items: (schema.duplicates?.items || []).map((item: any) => ({
                            document_id: item.document_id || item.document_number || "",
                            document_title: item.document_title || item.document_type || "",
                            cfop: item.cfop || "",
                            issue_date: item.issue_date || item.document_date || "",
                            xml_size_bytes: item.xml_size_bytes || 0,
                            assignor_name: item.assignor_name || "",
                            assignor_cnpj: item.assignor_cnpj || item.assignor_document || "",
                            debtor_cpf_cnpj: item.debtor_cpf_cnpj || item.debtor_document || "",
                            debtor_name: item.debtor_name || "",
                            debtor_email: item.debtor_email || "",
                            address_street: item.address_street || "",
                            address_number: item.address_number || "",
                            address_district: item.address_district || "",
                            address_ibge_code: item.address_ibge_code || "",
                            address_city: item.address_city || "",
                            address_state: item.address_state || "",
                            address_zip_code: item.address_zip_code || "",
                            address_country: item.address_country || "",
                            address_phone: item.address_phone || "",
                            address_complement: item.address_complement || "",
                            receivable_number: item.receivable_number || item.document_number || "",
                            receivable_due_date: item.receivable_due_date || "",
                            receivable_amount: parseFloat(item.receivable_amount || item.receivable_value) || 0,
                            receivable_v_pag: item.receivable_v_pag || "",
                            receivable_nat_op: item.receivable_nat_op || "",
                            receivable_ind_pag: item.receivable_ind_pag || "",
                            disbursement_value: parseFloat(item.disbursement_value || '0'),
                            fee_name: item.fee_name || "",
                            fee_value: parseFloat(item.fee_value || '0'),
                            guarantee_percent: parseFloat(item.guarantee_percent || '0'),
                            main_risk: item.main_risk || "",
                            external_link: item.external_link || ""
                        }))
                    },
                    disbursement: {
                        disbursements: schema.disbursement?.disbursements || [],
                        totalAmount: schema.disbursement?.totalAmount || schema.total_amount || data.nominal_value || 0
                    }
                };

                // Armazenar arquivos existentes do operation_dictionary
                if (schema.files) {
                    setExistingContractFile(schema.files.contract_file || '');
                    setExistingArtifactFiles(schema.files.artefacts || []);
                }

                // Armazenar dados dos participantes separadamente
                if (assignorParticipant) {
                    setAssignorData(assignorParticipant);
                    setSelectedCedente(assignorParticipant.person_id);
                }

                if (recipientData) {
                    setRecipientData(recipientData);
                    setSelectedDestinatario(recipientData.person_id);
                    form.value = {
                        ...form.value,
                        recipient: recipientData.name || ''
                    };
                }
            } else {
                toast.error('Erro ao carregar dados do contrato');
            }
        } catch (error) {
            toast.error('Erro ao conectar com o servidor');
        } finally {
            isLoadingForm.value = false;
        }
    };

    useEffect(() => {
        if (credit_operation_id) {
            fetchContractData();
        }
    }, [credit_operation_id]);

    const handleCancel = () => {
        navigate(-1);
    };

    const isFormDisabled = isContractDisabled(form.value.status);

    return (
        <Fragment>
            <PageForm
                pageTitle="Cessão de Recebíveis"
                apiVersion={apiVersion.value}
                isLoading={isLoadingForm.value}
                breadcrumb={[
                    { label: "Operações", url: "/operations" },
                    { label: "Cessão de Recebíveis", url: "#" }
                ]}
                controlSlot={
                    <div className="pt-4 flex justify-end">
                        <button
                            className="btn-cancel"
                            onClick={handleCancel}
                            disabled={isLoadingForm.value}
                        >
                            Voltar
                        </button>

                        {/* Dropdown de Ações */}
                        <div ref={dropdownRef} className="relative inline-block text-left ml-2">
                            <button
                                type="button"
                                className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                disabled={isLoadingForm.value}
                            >
                                Ações
                                <FaEllipsisV className="ml-2 h-4 w-4" />
                            </button>

                            {isDropdownOpen && (
                                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                                    <div className="py-1">
                                        <button
                                            className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => {
                                                handleSubmit();
                                                setIsDropdownOpen(false);
                                            }}
                                            disabled={isLoadingForm.value || isFormDisabled}
                                        >
                                            <FaSave className="mr-3 h-4 w-4 text-green-500" />
                                            Salvar
                                        </button>
                                        <button
                                            className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => {
                                                setIsCancelModalOpen(true);
                                                setIsDropdownOpen(false);
                                            }}
                                            disabled={isLoadingForm.value || !credit_operation_id || isFormDisabled}
                                        >
                                            <FaBan className="mr-3 h-4 w-4 text-orange-500" />
                                            Cancelar Contrato
                                        </button>
                                        <button
                                            className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => {
                                                setIsFinishModalOpen(true);
                                                setIsDropdownOpen(false);
                                            }}
                                            disabled={isLoadingForm.value || !credit_operation_id || isFormDisabled}
                                        >
                                            <FaCheck className="mr-3 h-4 w-4 text-green-500" />
                                            Finalizar Contrato
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                }
            >
                <ReceivablesDownloader
                    sequencialNumber={form.value.sequencial_number}
                    duplicatesCount={form.value.duplicates.items.filter(item =>
                        item.document_id && item.document_id.trim() !== '' && item.receivable_amount > 0
                    ).length}
                    onDataLoaded={handleJsonDataLoaded}
                />
                <div className="container mx-auto px-2 py-4">
                    <h2 className="text-xl font-semibold mt-4">Informações Gerais</h2>
                    <div className="mb-4 mt-2">

                        <BadgeStatus
                            color={getStatusColor(form.value.status)}
                            text={form.value.status || ''}
                        />
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-2">
                            <TextEdit
                                label="Número do Contrato"
                                id="sequencial_number"
                                value={form.value.sequencial_number.toString()}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="operation_number"
                                label="Código da Cessão"
                                value={form.value.operation_number}
                                onChange={(e) => handleChange("operation_number", e.target.value)}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="operation_dictionary_external_link"
                                label="Card JIRA"
                                value={form.value.external_link}
                                onChange={(e) => handleChange("external_link", e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="col-span-3">
                            <TextEdit
                                id="originador"
                                label="Originador"
                                value={form.value.originator}
                                onChange={(e) => handleChange("originator", e.target.value)}
                                placeholder=""
                            />
                        </div>
                        <div className="col-span-2 flex items-center pt-6">
                            <input
                                type="checkbox"
                                id="with_recourse"
                                checked={form.value.with_recourse}
                                onChange={(e) => handleChange("with_recourse", e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <label htmlFor="with_recourse" className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                Com Regresso
                            </label>
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="col-span-5">
                            <TextEdit
                                id="assignor"
                                label="Cedente"
                                value={form.value.assignor}
                                onChange={(e) => handleChange("assignor", e.target.value)}
                                disabled={true}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="col-span-6 flex items-end gap-2">
                            <div className="flex-1">
                                <TextEdit
                                    id="recipient"
                                    label="Beneficiário"
                                    value={form.value.recipient}
                                    disabled={true}
                                />
                            </div>
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 mb-4"
                                title="Buscar beneficiário"
                            >
                                <FaSearch />
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="col-span-2">
                            <TextEdit
                                label="Data de Criação"
                                value={form.value.created_at}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="emission_date"
                                label="Data de Emissão"
                                value={form.value.emission_date}
                                onChange={(e) => handleChange("emission_date", applyDateMask(e.target.value))}

                            />
                        </div>
                        <div className="col-span-2">
                            <CurrencyInput
                                id="operation_dictionary_guarantee_percentage"
                                label="% aval da operação"
                                prefix="%"
                                fractionDigits={2}
                                value={form.value.guarantee_percentage}
                                onChange={(value) => handleChange("guarantee_percentage", value)}
                            />
                        </div>
                    </div>

                    <h2 className="text-xl font-semibold mt-4 mb-4 dark:text-white">Valor Financiado</h2>
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <CurrencyInput
                                id="loan_amount"
                                label="Valor Financiado"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.loan_amount}
                                onChange={(value) => handleChange("loan_amount", value)}
                            />
                        </div>
                    </div>

                    <DisbursementManager
                        disbursements={form.value.disbursement.disbursements}
                        totalAmount={form.value.disbursement.totalAmount}
                        onTotalAmountChange={(value) => {
                            form.value = {
                                ...form.value,
                                disbursement: {
                                    ...form.value.disbursement,
                                    totalAmount: value
                                }
                            };
                        }}
                        onAdd={(disbursement) => {
                            const currentDisbursements = [...form.value.disbursement.disbursements, disbursement];
                            form.value = {
                                ...form.value,
                                disbursement: { ...form.value.disbursement, disbursements: currentDisbursements }
                            };
                        }}
                        onUpdate={(index, disbursement) => {
                            const disbursements = form.value.disbursement.disbursements.map((d, i) => i === index ? disbursement : d);
                            form.value = {
                                ...form.value,
                                disbursement: { ...form.value.disbursement, disbursements }
                            };
                        }}
                        onRemove={(index) => {
                            const disbursements = form.value.disbursement.disbursements.filter((_, i) => i !== index);
                            form.value = {
                                ...form.value,
                                disbursement: { ...form.value.disbursement, disbursements }
                            };
                        }}
                        disabled={isLoadingForm.value}
                    />

                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-2">
                            <CurrencyInput
                                label="Total dos Desembolsos"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.disbursement.totalAmount - (form.value.rebate_amount || 0)}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-2">
                            <CurrencyInput
                                id="operation_dictionary_rebate_amount"
                                label="Valor do Abatimento"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.rebate_amount}
                                onChange={(value) => handleChange("rebate_amount", value)}
                            />
                        </div>
                    </div>

                    <h2 className="text-xl font-semibold mt-4">Vencimento</h2>
                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-2">
                            <CurrencyInput
                                id="operation_dictionary_total_amount"
                                label="Valor Total"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.total_amount}
                                onChange={(value) => handleChange("total_amount", value)}
                            />
                        </div>
                        {form.value.with_recourse && (
                            <div className="col-span-2">
                                <TextEdit
                                    id="expiration_date"
                                    label="Data de Vencimento"
                                    value={form.value.expiration_date}
                                    onChange={(e) => handleChange("expiration_date", applyDateMask(e.target.value))}

                                />
                            </div>
                        )}
                    </div>

                    <ContractFileUpload
                        contractFiles={contractFiles}
                        existingFile={existingContractFile}
                        removedFile={removedContractFile}
                        onRemove={() => setRemovedContractFile(true)}
                        onRestore={() => setRemovedContractFile(false)}
                        creditOperationId={credit_operation_id || ''}
                        onDownload={handleDownload}
                    />

                    <ArtifactFilesUpload
                        artifactFiles={artifactFiles}
                        existingFiles={existingArtifactFiles}
                        removedFiles={removedArtifactFiles}
                        onRemoveExisting={(file) => setRemovedArtifactFiles([...removedArtifactFiles, file])}
                        onRestoreExisting={(file) => setRemovedArtifactFiles(removedArtifactFiles.filter(f => f !== file))}
                        creditOperationId={credit_operation_id || ''}
                        onDownload={handleDownload}
                    />

                    <DuplicatesTable
                        duplicates={form.value.duplicates?.items || []}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onRowClick={(duplicate) => {
                            setSelectedDuplicate(duplicate);
                            setIsDuplicateModalOpen(true);
                        }}
                    />

                </div>

                <PersonSearchModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSelect={handleSelectPerson}
                />

                {/* Modal de visualização de duplicata */}
                <DuplicateModalView
                    isOpen={isDuplicateModalOpen}
                    onClose={() => {
                        setIsDuplicateModalOpen(false);
                        setSelectedDuplicate(null);
                    }}
                    duplicate={selectedDuplicate}
                />

            </PageForm>
            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onCancel={() => setIsCancelModalOpen(false)}
                onConfirm={handleCancelContract}
                id="cancel-contract-modal"
                title="Cancelar Contrato"
                text="Tem certeza que deseja cancelar este contrato?"
            />
            <ConfirmationModal
                isOpen={isFinishModalOpen}
                onCancel={() => setIsFinishModalOpen(false)}
                onConfirm={handleFinishContract}
                id="finish-contract-modal"
                title="Finalizar Contrato"
                text="Tem certeza que deseja finalizar este contrato?"
            />
        </Fragment>);
};

export default ReceivablesAssignmentPage;